import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import debounce from 'lodash/debounce';
import scogo_apis from '../../scogo_apis';

export const transformAddress = (formValues, addressName) => {
    const address = formValues[addressName];
    const additionalAddress = formValues[`${addressName}:additional_address`] ? formValues[`${addressName}:additional_address`] : '';
    return {
        place_id: formValues[`${addressName}:placeId`],
        plain_text: additionalAddress ? `${additionalAddress}, ${address}` : address || '',
    };
};

const Address = ({ id, name, label, required, placeholder, validate, className = '', shouldUnregister, onChange = () => { }, rows = 1 }) => {
    const { register, setValue, formState: { errors } } = useFormContext();

    const error = errors[name]?.message || '';

    const [suggestions, setSuggestions] = useState([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);

    const wrapperRef = useRef(null);

    const fetchSuggestions = useCallback(
        debounce(async (input) => {
            try {
                const response = await scogo_apis.admin.suggestAddress({}, {}, {}, { input });
                if (response.status === 200 && response.data.data) {
                    setSuggestions(response.data.data);
                } else {
                    setSuggestions([]);
                }
            } catch (err) {
                console.error("Error fetching suggestions:", err);
                setSuggestions([]);
            }
        }, 300),
        []
    );

    const handleChange = (event) => {
        const value = event.target.value;
        setValue(name, value);
        onChange(value);
        if (value) {
            fetchSuggestions(value);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionSelect = (suggestion) => {
        setSelectedSuggestion(suggestion);
        setValue(name, suggestion.text);
        setValue(`${name}:placeId`, suggestion.placeId);
        setSuggestions([]);
        onChange(suggestion.text);
    };

    // ✅ Close suggestions on click away:
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setSuggestions([]); // Close suggestions
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    return (
        <>
            <div className={`block px-2 ${className}`} ref={wrapperRef}>
                {label && (
                    <label htmlFor={id} className='text-scogo99 font-normal text-font09 md:text-font11'>
                        {label} {required && <span className='text-scogoorange'>*</span>}
                    </label>
                )}
                <div className="relative">
                    <textarea
                        id={id}
                        className='text-scogo99 font-normal text-font09 md:text-font11 block w-full focus:ring-0 px-0 border-scogoddd border-b-2 border-t-0 border-r-0 border-l-0 focus:border-scogoprimary'
                        {...register(name, {
                            required: { value: required, message: 'Required' },
                            validate,
                            shouldUnregister,
                        })}
                        placeholder={placeholder}
                        onChange={handleChange}
                        name={name}
                        rows={rows}
                    />
                    {suggestions.length > 0 && (
                        <ul className="absolute z-10 w-full bg-white shadow-lg rounded-md mt-1 max-h-60 overflow-y-auto">
                            {suggestions.map(({ placeId, text }, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleSuggestionSelect({ placeId, text })}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-scogo99 text-font09"
                                >
                                    {text}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {error && <p className='text-scogoclosed text-font09 font-normal h-6'>{error}</p>}
            </div>

            {selectedSuggestion && (
                <div className={`block px-2 ${className}`}>
                    <label htmlFor={id} className='text-scogo99 font-normal text-font09 md:text-font11'>
                        Enter house/flat no., floor, building name etc. {required && <span className='text-scogoorange'>*</span>}
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            className='text-scogo99 font-normal text-font09 md:text-font11 block w-full focus:ring-0 px-0 border-scogoddd border-b-2 border-t-0 border-r-0 border-l-0 focus:border-scogoprimary mt-2'
                            name={`${name}:additional_address`}
                            {...register(`${name}:additional_address`, {
                                required: { value: required, message: 'Required' },
                                validate,
                                shouldUnregister,
                            })}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default Address;
